import { AutomationStep } from '@/types/automationSteps.ts';
import { Stack, Text } from '@mantine/core';
import { useContext, useState } from 'react';
import StyledModal from '@/components/StyledModal/StyledModal.tsx';
import ModalConfirmationButtons from '@/components/StyledModal/ModalConfirmationButtons.tsx';
import useStaticDatasetUsage from '@/hooks/api/datasets/static/useStaticDatasetUsage.ts';
import useDeleteStaticDataset from '@/hooks/api/datasets/static/useDeleteStaticDataset.ts';
import EditVersionContext from '@/context/EditVersionContext/EditVersionContext.tsx';
import { collectDeleteImpacts } from '@/utils/deleteImpacts.ts';
import { StaticDataset } from '@/types/datasets.ts';
import StaticDatasetUsageDetails from './StaticDatasetUsageDetails.tsx';
import StaticDatasetHistory from './StaticDatasetHistory.tsx';

export default function RemoveStaticDatasetModal({
  dataset,
  onClose,
}: {
  dataset: StaticDataset;
  onClose: () => void;
}) {
  const usage = useStaticDatasetUsage(dataset.id);
  const report = usage.isError || usage.fetchStatus === 'paused' ? undefined : usage.data;
  const item = report?.results.find((entry) => entry.static_dataset_id === dataset.id);
  const editor = useContext(EditVersionContext);
  const version = editor?.editingVersion;
  const localReferences = [
    { label: 'Editor draft', steps: version?.steps ?? [] },
    ...(version?.local_subflows ?? []).map((subflow) => ({
      label: subflow.name || 'Saved subflow',
      steps: subflow.steps as AutomationStep[],
    })),
  ].flatMap(({ label, steps }) =>
    collectDeleteImpacts(
      steps,
      { type: 'static_dataset', staticDatasetId: dataset.id },
      { includeInactiveFields: true },
    ).map((impact) => ({ ...impact, scopeLabel: label })),
  );
  const [message, setMessage] = useState('');
  const removal = useDeleteStaticDataset({
    onSuccess: onClose,
    onError: () => {
      setMessage(
        'Removal was not completed. Usage or data may have changed; review the refreshed details before trying again.',
      );
      void usage.refetch();
    },
  });
  const blocked =
    usage.fetchStatus !== 'idle' ||
    usage.isError ||
    !usage.data?.active_complete ||
    item?.removal_disposition !== 'confirm_removal' ||
    item.dataset_id !== dataset.dataset.id ||
    !!editor?.hasPendingEdits ||
    !!editor?.staleDraft ||
    localReferences.length > 0;
  const closeUnlessPending = () => {
    if (!removal.isPending) onClose();
  };
  return (
    <StyledModal opened onClose={closeUnlessPending} title="Remove data source">
      <Stack gap="sm">
        <Text size="sm" style={{ overflowWrap: 'anywhere' }}>
          {item?.name ?? dataset.dataset.name}
        </Text>
        {item && item.dataset_id !== dataset.dataset.id && (
          <Text size="sm">
            This source was replaced. Close this dialog and review the refreshed source before
            removing it.
          </Text>
        )}
        <StaticDatasetUsageDetails
          report={report}
          item={item}
          removal
          loading={usage.isFetching}
          failed={usage.isError}
          paused={usage.fetchStatus === 'paused'}
          onRefresh={() => {
            void usage.refetch();
          }}
        />
        <Text size="sm" c="dimmed">
          Removal takes this source out of the automation. Backing data and completed run evidence
          remain, but older versions may require restoring the source before they can run again.
        </Text>
        <StaticDatasetHistory datasetId={dataset.id} />
        {(editor?.hasPendingEdits || editor?.staleDraft) && (
          <Text size="sm">Save or reload the editor before removing a source.</Text>
        )}
        {localReferences.length > 0 && (
          <Text size="sm">
            This editor snapshot references the source. Remove or change these inputs first
            (including disabled saved inputs):{' '}
            {localReferences.map((impact) => `${impact.scopeLabel}: ${impact.stepName}`).join('; ')}
            .
          </Text>
        )}
        {message && (
          <Text size="sm" role="alert">
            {message}
          </Text>
        )}
      </Stack>
      <ModalConfirmationButtons
        confirmText="Remove"
        confirmType="delete"
        closeOnConfirm={false}
        confirmDisabled={blocked}
        confirmLoading={removal.isPending}
        cancelButtonProps={{ disabled: removal.isPending }}
        onClose={closeUnlessPending}
        onConfirm={() => {
          if (!blocked && item)
            removal.mutate({ staticDatasetId: dataset.id, expectedDatasetId: item.dataset_id });
        }}
      />
    </StyledModal>
  );
}
